import type { StyleSpecification } from "maplibre-gl";
import { addProtocol } from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import { reportError, shouldSkipReporting, toReportableError } from "util/error-tracking";
import {
    abortBasemapManifestLoad,
    isAbortError,
    loadBasemapManifest,
} from "./basemap-manifest";
import { BasemapManifest, buildSelfHostedMapStyle } from "./map-style";

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

export type BasemapState =
    | { status: "ready"; style: StyleSpecification }
    | { status: "loading" }
    | { status: "unavailable" };

type ManifestState =
    | { status: "loading" | "failed" }
    | { status: "loaded"; manifest: BasemapManifest };

const reportManifestFailure = (e: unknown): void => {
    if (shouldSkipReporting(e)) {
        return;
    }
    const error = toReportableError(e);
    reportError(error, { fingerprint: `basemap-manifest:${error.name}` });
};

const useBasemapManifest = (): ManifestState => {
    const [manifest, setManifest] = React.useState<BasemapManifest | null>(null);
    const [manifestFailed, setManifestFailed] = React.useState(false);
    React.useEffect(() => {
        let active = true;
        const load = () => {
            loadBasemapManifest()
                .then(result => {
                    if (active) {
                        setManifest(result);
                    }
                })
                .catch((e: unknown) => {
                    if (isAbortError(e)) {
                        return;
                    }
                    if (active) {
                        setManifestFailed(true);
                    }
                    reportManifestFailure(e);
                });
        };
        const onPageHide = () => abortBasemapManifestLoad();
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted && active) {
                load();
            }
        };
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);
        load();
        return () => {
            active = false;
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
        };
    }, []);
    return React.useMemo((): ManifestState => {
        if (manifest) {
            return { status: "loaded", manifest };
        }
        return { status: manifestFailed ? "failed" : "loading" };
    }, [manifest, manifestFailed]);
};

const basemapFor = (manifest: ManifestState): BasemapState => {
    switch (manifest.status) {
        case "loaded":
            ensurePmtilesProtocol();
            return {
                status: "ready",
                style: buildSelfHostedMapStyle(manifest.manifest.tiles),
            };
        case "loading":
            return { status: "loading" };
        case "failed":
            return { status: "unavailable" };
        default: {
            const exhaustiveCheck: never = manifest;
            throw new Error(
                `Unhandled manifest state ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

export const useBasemap = (): BasemapState => {
    const manifest = useBasemapManifest();
    return React.useMemo(() => basemapFor(manifest), [manifest]);
};
